/**
 * GovernanceEditor — Live Governance Rules Editor for Evidence Vault
 * Full institutional admin interface for scoring weights, thresholds,
 * expiry rules, readiness thresholds, and version history.
 */
import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Shield, Save, RotateCcw, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, Clock } from "lucide-react";

const apiBase = () => process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem("decarboniq_token")}` });
const apiFetch = (path, opts = {}) =>
  fetch(`${apiBase()}/api${path}`, { ...opts, headers: { "Content-Type": "application/json", ...authH(), ...(opts.headers || {}) } });

// Numeric input with label + guardrails
function NumInput({ label, value, onChange, min=0, max=1, step=0.01, unit="", note="" }) {
  return (
    <div className="flex items-start justify-between py-2 border-b border-slate-50 last:border-0 gap-4">
      <div className="min-w-0">
        <p className="text-xs font-medium text-slate-700">{label}</p>
        {note && <p className="text-[9px] text-slate-400 mt-0.5">{note}</p>}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <input
          type="number" value={value} min={min} max={max} step={step}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          className="w-20 text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white text-right focus:outline-none focus:ring-1 focus:ring-emerald-400"
          data-testid={`gov-input-${label.toLowerCase().replace(/\s/g,"-")}`}
        />
        {unit && <span className="text-[10px] text-slate-400">{unit}</span>}
      </div>
    </div>
  );
}

// Confirmation Modal
function ConfirmModal({ onConfirm, onCancel, changeNote, setChangeNote }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-center">
            <AlertTriangle size={18} className="text-amber-600" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-800">Confirm Governance Update</p>
            <p className="text-[10px] text-slate-400">This will create an immutable version snapshot</p>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
          Changing governance rules affects all confidence scores and readiness flags platform-wide.
          The previous config will be automatically snapshotted and preserved for audit.
        </div>
        <div>
          <p className="text-xs font-medium text-slate-700 mb-1.5">Change Note (required)</p>
          <input
            value={changeNote}
            onChange={e => setChangeNote(e.target.value)}
            placeholder="Describe what was changed and why…"
            className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-400"
            data-testid="change-note-input"
            autoFocus
          />
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={onCancel} className="flex-1 text-xs border border-slate-200 rounded-lg py-2 text-slate-600 hover:bg-slate-50 font-medium">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!changeNote.trim()}
            className="flex-1 text-xs bg-[#0f172a] text-white rounded-lg py-2 font-bold hover:bg-slate-800 disabled:opacity-60"
            data-testid="confirm-save-btn"
          >
            Save & Snapshot
          </button>
        </div>
      </div>
    </div>
  );
}

// Snapshot diff viewer (simple before/after)
function SnapshotDiff({ snapshot }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = snapshot.config || {};
  return (
    <div className="border border-slate-100 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 text-left hover:bg-slate-50"
      >
        <div>
          <p className="text-xs font-medium text-slate-700">Version {snapshot.version}</p>
          <p className="text-[10px] text-slate-400">{snapshot.changed_at?.slice(0, 16).replace("T"," ")} · {snapshot.changed_by} · {snapshot.change_note?.slice(0,60)}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded">v{snapshot.version}</span>
          {expanded ? <ChevronUp size={12} className="text-slate-400" /> : <ChevronDown size={12} className="text-slate-400" />}
        </div>
      </button>
      {expanded && (
        <div className="border-t border-slate-100 p-3 bg-slate-50 space-y-1">
          {cfg.scoring && (
            <div className="text-[10px] text-slate-500">
              <span className="font-bold">Scoring weights: </span>
              approval={cfg.scoring.approval_rate_weight} · checklist={cfg.scoring.checklist_rate_weight} · critical={cfg.scoring.critical_rate_weight}
            </div>
          )}
          {cfg.readiness?.vcm_ready && (
            <div className="text-[10px] text-slate-500">
              <span className="font-bold">VCM: </span>score≥{cfg.readiness.vcm_ready.min_score} · approved≥{cfg.readiness.vcm_ready.min_approved}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function GovernanceEditor() {
  const [config, setConfig]       = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState("scoring");
  const [showConfirm, setShowConfirm] = useState(false);
  const [changeNote, setChangeNote]   = useState("");
  const [saving, setSaving]       = useState(false);

  // Local editable state
  const [scoring, setScoring]     = useState({});
  const [thresholds, setThresholds] = useState({});
  const [readiness, setReadiness] = useState({});
  const [expiryDays, setExpiryDays] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgR, snapsR] = await Promise.all([
        apiFetch("/lagos/vault/governance/config"),
        apiFetch("/lagos/vault/governance/snapshots"),
      ]);
      if (cfgR.ok) {
        const d = await cfgR.json();
        setConfig(d);
        setScoring({ ...d.scoring });
        setThresholds({ ...d.thresholds });
        setReadiness({ ...d.readiness });
        setExpiryDays({ ...d.expiry_days });
      }
      if (snapsR.ok) {
        const d = await snapsR.json();
        setSnapshots(d.snapshots || []);
      }
    } catch { toast.error("Failed to load governance config"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!changeNote.trim()) return;
    setSaving(true);
    try {
      const r = await apiFetch("/lagos/vault/governance/config", {
        method: "PUT",
        body: JSON.stringify({
          scoring, thresholds, readiness, expiry_days: expiryDays, change_note: changeNote
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      const d = await r.json();
      toast.success(`Governance config updated to v${d.version}`);
      setShowConfirm(false);
      setChangeNote("");
      load();
    } catch (e) { toast.error("Save failed: " + e.message); }
    finally { setSaving(false); }
  };

  const rollback = async (snapshotId) => {
    if (!window.confirm("Restore this snapshot? This will create a new version.")) return;
    try {
      const r = await apiFetch(`/lagos/vault/governance/snapshots/${snapshotId}`);
      if (!r.ok) throw new Error();
      const snap = await r.json();
      const cfg = snap.config || {};
      const note = `Rollback to v${snap.version}`;
      const saveR = await apiFetch("/lagos/vault/governance/config", {
        method: "PUT",
        body: JSON.stringify({ ...cfg, change_note: note }),
      });
      if (!saveR.ok) throw new Error();
      toast.success(`Rolled back to v${snap.version}`);
      load();
    } catch { toast.error("Rollback failed"); }
  };

  if (loading) return <div className="p-4 text-slate-400 text-xs">Loading governance config…</div>;

  const TABS = [
    { id:"scoring",   label:"Scoring Weights" },
    { id:"readiness", label:"Readiness Rules" },
    { id:"expiry",    label:"Expiry Rules" },
    { id:"history",   label:`Version History (${snapshots.length})` },
  ];

  const weightSum = (scoring.approval_rate_weight || 0) + (scoring.checklist_rate_weight || 0) + (scoring.critical_rate_weight || 0);
  const weightValid = Math.abs(weightSum - 1.0) < 0.01;

  return (
    <div className="space-y-4" data-testid="governance-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold text-slate-700">Live Governance Rules Editor</p>
          <p className="text-[10px] text-slate-400">Config v{config?.version} · Every save creates an immutable snapshot</p>
        </div>
        <button
          onClick={() => setShowConfirm(true)}
          className="flex items-center gap-2 bg-emerald-700 text-white text-xs px-4 py-2 rounded-lg font-bold hover:bg-emerald-800"
          data-testid="save-governance-btn"
        >
          <Save size={11} /> Save Changes
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex-1 py-1.5 px-2 rounded-lg text-[11px] font-medium transition-all ${activeTab === t.id ? "bg-[#0f172a] text-white" : "text-slate-500 hover:bg-slate-50"}`}
            data-testid={`gov-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Scoring Weights ───────────────────────────────────────────────────── */}
      {activeTab === "scoring" && (
        <div className="bg-white border border-slate-100 rounded-xl p-4">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3">Confidence Score Weights</p>
          {!weightValid && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3 text-xs text-amber-700 flex items-center gap-2">
              <AlertTriangle size={12} />
              Weights sum to {weightSum.toFixed(2)} — must equal 1.00
            </div>
          )}
          {weightValid && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-2 mb-3 text-xs text-emerald-700 flex items-center gap-2">
              <CheckCircle2 size={12} />
              Weights sum to 1.00 ✓
            </div>
          )}
          <NumInput label="Approval Rate Weight" value={scoring.approval_rate_weight || 0.35}
            onChange={v => setScoring(s => ({...s, approval_rate_weight: v}))}
            note="approved docs / active docs (35% default)" />
          <NumInput label="Checklist Rate Weight" value={scoring.checklist_rate_weight || 0.40}
            onChange={v => setScoring(s => ({...s, checklist_rate_weight: v}))}
            note="checklist items completed / total (40% default)" />
          <NumInput label="Critical Coverage Weight" value={scoring.critical_rate_weight || 0.25}
            onChange={v => setScoring(s => ({...s, critical_rate_weight: v}))}
            note="critical items complete / total critical (25% default)" />
          <p className="text-[10px] text-slate-400 mt-3 bg-slate-50 rounded-lg p-2">
            Formula: score = approval×{(scoring.approval_rate_weight||0)*100}% + checklist×{(scoring.checklist_rate_weight||0)*100}% + critical×{(scoring.critical_rate_weight||0)*100}%
          </p>
        </div>
      )}

      {/* ── Readiness Rules ───────────────────────────────────────────────────── */}
      {activeTab === "readiness" && (
        <div className="space-y-4">
          {[
            { key:"vcm_ready",  label:"VCM Ready", color:"#059669" },
            { key:"art6_ready", label:"Art.6.4 Pre-Qualified", color:"#1d4ed8" },
            { key:"bond_ready", label:"Green Bond Ready", color:"#7c3aed" },
          ].map(({ key, label, color }) => {
            const r = readiness[key] || {};
            return (
              <div key={key} className="bg-white border border-slate-100 rounded-xl p-4">
                <p className="text-xs font-bold mb-3" style={{ color }}>{label}</p>
                <NumInput label="Min Score" value={r.min_score || 0} onChange={v => setReadiness(rd => ({...rd, [key]: {...rd[key], min_score: v}}))}
                  min={0} max={100} step={1} unit="/100" />
                <NumInput label="Min Approved Docs" value={r.min_approved || 0} onChange={v => setReadiness(rd => ({...rd, [key]: {...rd[key], min_approved: v}}))}
                  min={0} max={20} step={1} unit="docs" />
                <NumInput label="Min Critical Coverage" value={r.min_critical_rate || 0} onChange={v => setReadiness(rd => ({...rd, [key]: {...rd[key], min_critical_rate: v}}))}
                  min={0} max={100} step={5} unit="%" />
              </div>
            );
          })}
        </div>
      )}

      {/* ── Expiry Rules ──────────────────────────────────────────────────────── */}
      {activeTab === "expiry" && (
        <div className="bg-white border border-slate-100 rounded-xl p-4">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3">Document Expiry Days by Type</p>
          <div className="space-y-0">
            {Object.entries(expiryDays).map(([docType, days]) => (
              <NumInput
                key={docType}
                label={docType.replace(/_/g, " ")}
                value={days}
                onChange={v => setExpiryDays(ed => ({...ed, [docType]: Math.round(v)}))}
                min={7} max={730} step={1} unit="days"
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Version History ───────────────────────────────────────────────────── */}
      {activeTab === "history" && (
        <div className="space-y-2">
          <p className="text-[10px] text-slate-400 flex items-center gap-1"><Clock size={10} /> Immutable version history — all snapshots preserved</p>
          {snapshots.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No snapshots yet</p>
          ) : snapshots.map(snap => (
            <div key={snap.id} className="relative">
              <SnapshotDiff snapshot={snap} />
              <button
                onClick={() => rollback(snap.id)}
                className="absolute right-3 top-3 text-[9px] bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded font-bold hover:bg-amber-100"
                data-testid={`rollback-${snap.id}`}
                title="Restore this version"
              >
                <RotateCcw size={8} className="inline mr-1" />Restore
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Confirmation modal */}
      {showConfirm && (
        <ConfirmModal
          changeNote={changeNote}
          setChangeNote={setChangeNote}
          onConfirm={handleSave}
          onCancel={() => { setShowConfirm(false); setChangeNote(""); }}
        />
      )}
    </div>
  );
}
